import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';

import SelectBox from '../shared/select-box/SelectBox';
import SelectBoxList from '../shared/select-box/SelectBoxList';

import userRoles from '../../constants/userRoles';
import { getUserDistributorIds } from '../../util/user';

const normalizeAssignmentIds = (ids) => (Array.isArray(ids) ? ids.filter(Boolean) : []);

const resolveAssignments = (ids, availableItems, idField) => {
  const itemsById = new Map(
    (availableItems || [])
      .filter(Boolean)
      .map((item) => [item?.[idField], item])
      .filter(([id]) => Boolean(id))
  );

  return normalizeAssignmentIds(ids)
    .map((id) => itemsById.get(id))
    .filter(Boolean);
};

const mergeVisibleAndUnresolvedAssignmentIds = (selectedItems, originalIds, availableItems, idField) => {
  const availableIds = new Set(
    (availableItems || [])
      .filter(Boolean)
      .map((item) => item?.[idField])
      .filter(Boolean)
  );
  const unresolvedIds = normalizeAssignmentIds(originalIds).filter((id) => !availableIds.has(id));
  const selectedIds = (selectedItems || [])
    .filter(Boolean)
    .map((item) => item?.[idField])
    .filter(Boolean);

  return [...new Set([...unresolvedIds, ...selectedIds])];
};

const SurgeonModal = (props) => {
  const {
    mode = 'create',
    open,
    onClose,
    onSubmit,
    loading,
    distributors = [],
    users = [],
    hospitalsList = [],
    currentUser,
    initialValues,
  } = props;

  const [view, setView] = useState(0);
  const [user, setUser] = useState(null);
  const [distributor, setDistributor] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [managers, setManagers] = useState([]);
  const [registrars, setRegistrars] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  // const [manufacturer, setManufacturer] = useState(null);
  // const [engineers, setEngineers] = useState([]);
  // const [logistics, setLogistics] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && mode === 'update') {
      setUser(initialValues?.userId ? users.find((u) => u.uid === initialValues.userId) : null);
      setDistributor(initialValues?.distributor ? distributors.find((d) => d.id === initialValues.distributor) : null);
      setHospitals(resolveAssignments(initialValues?.hospitals, hospitalsList, 'id'));
      setManagers(resolveAssignments(initialValues?.manager, users, 'uid'));
      setRegistrars(resolveAssignments(initialValues?.registrar, users, 'uid'));
      setSalesReps(resolveAssignments(initialValues?.salesRep, users, 'uid'));
      // setManufacturer(initialValues?.manufacturer ? users.find((u) => u.uid === initialValues.manufacturer) : null);
      // setLogistics(initialValues?.logistics ? users.find((u) => u.uid === initialValues.logistics) : null);
      // setEngineers(initialValues?.engineer ? initialValues.engineer?.map((id) => users.find((u) => u.uid === id)) : []);
    }
  }, [open, mode, initialValues, users, distributors, hospitalsList]);

  const resetValues = () => {
    setUser(null);
    setDistributor(null);
    setHospitals([]);
    setManagers([]);
    setRegistrars([]);
    setSalesReps([]);
    // setManufacturer(null);
    // setEngineers([]);
    // setLogistics(null);
    setView(0);
  };

  const handleSubmit = async () => {
    try {
      const originalHospitalIds = distributor?.id === initialValues?.distributor ? initialValues?.hospitals : [];
      const formObj = {
        userId: user?.uid || '',
        distributor: distributor?.id || '',
        hospitals: mergeVisibleAndUnresolvedAssignmentIds(hospitals, originalHospitalIds, hospitalsList, 'id'),
        manager: mergeVisibleAndUnresolvedAssignmentIds(managers, initialValues?.manager, users, 'uid'),
        registrar: mergeVisibleAndUnresolvedAssignmentIds(registrars, initialValues?.registrar, users, 'uid'),
        salesRep: mergeVisibleAndUnresolvedAssignmentIds(salesReps, initialValues?.salesRep, users, 'uid'),
        // manufacturer: manufacturer || '',
        // engineer: engineers?.map((e) => e.uid) || [],
        // logistics: logistics || '',
      };

      await onSubmit(formObj);
      resetValues();
    } catch (err) {}
  };

  const isDisabled = () => {
    return !user || !distributor;
  };

  const surgeonsOptions = useMemo(() => users.filter((u) => {
    let persistence = true;
    if (currentUser.role === userRoles.GLOBAL_ADMIN.name) {
      persistence = true;
    }
    if (currentUser.role === userRoles.COUNTRY_ADMIN.name || currentUser.role === userRoles.DISTRIBUTOR_ADMIN.name) {
      persistence = !u.company || u.company === currentUser.company;
    }
    if (currentUser.role === userRoles.DISTRIBUTOR_ADMIN.name) {
      const currentUserDistributorIds = getUserDistributorIds(currentUser);
      const userDistributorIds = getUserDistributorIds(u);
      persistence = persistence && userDistributorIds.some((id) => currentUserDistributorIds.includes(id));
    }
    return persistence && u.role === userRoles.SURGEON.name;
  }), [users, currentUser]);

  const salesRepsOptions = useMemo(() => users.filter((u) => {
    let persistence = true;
    if (currentUser.role === userRoles.GLOBAL_ADMIN.name) {
      persistence = true;
    }
    if (currentUser.role === userRoles.COUNTRY_ADMIN.name || currentUser.role === userRoles.DISTRIBUTOR_ADMIN.name) {
      persistence = !u.company || u.company === currentUser.company;
    }
    return persistence && u.role === userRoles.SALES_REP.name;
  }), [users, currentUser]);

  const managersOptions = useMemo(() => users.filter((u) => {
    let persistence = true;
    if (currentUser.role === userRoles.GLOBAL_ADMIN.name) {
      persistence = true;
    }
    if (currentUser.role === userRoles.COUNTRY_ADMIN.name || currentUser.role === userRoles.DISTRIBUTOR_ADMIN.name) {
      persistence = !u.company || u.company === currentUser.company;
    }
    return persistence && u.role === userRoles.PRACTICE_MANAGER.name;
  }), [users, currentUser]);

  const registrarsOptions = useMemo(() => users.filter((u) => {
    let persistence = true;
    if (currentUser.role === userRoles.GLOBAL_ADMIN.name) {
      persistence = true;
    }
    if (currentUser.role === userRoles.COUNTRY_ADMIN.name || currentUser.role === userRoles.DISTRIBUTOR_ADMIN.name) {
      persistence = !u.company || u.company === currentUser.company;
    }
    return persistence && u.role === userRoles.REGISTRAR.name;
  }), [users, currentUser]);

  const onHospitalSelect = (value) => {
    if (value && !hospitals?.find((hospital) => hospital?.id === value?.id)) {
      setHospitals([...(hospitals || []).filter(Boolean), value]);
    }
  };

  const onHospitalRemove = (id) => {
    setHospitals((hospitals || []).filter((hospital) => hospital?.id !== id));
    setSearch('');
  };

  // const distributorsOptions = useMemo(() => {
  //   if (currentUser.role === userRoles.DISTRIBUTOR_ADMIN.name) {
  //     return distributors?.filter((d) => currentUser?.distributors?.includes(d.id));
  //   }
  //   return distributors;
  // }, [distributors, currentUser]);

  const distributorsOptions = useMemo(() => {
    return distributors.filter((d) => user?.distributors?.includes(d.id));
  }, [distributors, user]);

  const hospitalsOptions = useMemo(() => {
    if (!distributor) {
      return [];
    }
    return hospitalsList.filter((hospital) => !hospitals?.find((h) => h?.id === hospital?.id) && distributor?.country === hospital?.country && distributor?.company === hospital?.company);
  }, [hospitalsList, hospitals, distributor]);

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      title={mode === 'update' ? 'Edit Surgeon' : 'Create Surgeon'}
      subtitle="Enter surgeon details"
      submitText={mode === 'update' ? 'Save Changes' : 'Create'}
      disabled={isDisabled()}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' } }}>
        {view === 0 ? (
          <Grid container spacing={1} sx={{ maxWidth: '560px' }}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={user}
                onChange={setUser}
                options={surgeonsOptions}
                label="Select Surgeon"
                required
                getOptionLabel={(option) => `${option?.firstName} ${option?.lastName} (${option?.email})`}
                groupBy={(option) =>
                  option?.lastName?.[0]?.toUpperCase() ||
                  option?.firstName?.[0]?.toUpperCase()
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={distributor}
                onChange={(value) => {
                  setDistributor(value);
                  setHospitals([]);
                }}
                options={distributorsOptions}
                label="Distributor"
                getOptionLabel={(option) => option.name}
                required
              />
            </Grid>
            {/*<Grid size={{ xs: 12 }}>*/}
            {/*  <Autocomplete*/}
            {/*    value={engineers}*/}
            {/*    onChange={setEngineers}*/}
            {/*    options={engineersOptions}*/}
            {/*    label="Engineers"*/}
            {/*    getOptionLabel={(option) => `${option?.firstName} ${option?.lastName}`}*/}
            {/*    groupBy={(option) =>*/}
            {/*      option?.lastName?.[0]?.toUpperCase() ||*/}
            {/*      option?.firstName?.[0]?.toUpperCase()*/}
            {/*    }*/}
            {/*    multiple*/}
            {/*  />*/}
            {/*</Grid>*/}
            {/*<Grid size={{ xs: 12 }}>*/}
            {/*  <Autocomplete*/}
            {/*    value={manufacturer}*/}
            {/*    onChange={setManufacturer}*/}
            {/*    options={manufacturersOptions}*/}
            {/*    label="Manufacturer"*/}
            {/*    getOptionLabel={(option) => `${option?.firstName} ${option?.lastName}`}*/}
            {/*    groupBy={(option) =>*/}
            {/*      option?.lastName?.[0]?.toUpperCase() ||*/}
            {/*      option?.firstName?.[0]?.toUpperCase()*/}
            {/*    }*/}
            {/*  />*/}
            {/*</Grid>*/}
            {/*<Grid size={{ xs: 12 }}>*/}
            {/*  <Autocomplete*/}
            {/*    value={logistics}*/}
            {/*    onChange={setLogistics}*/}
            {/*    options={logisticsOptions}*/}
            {/*    label="Logistics"*/}
            {/*    getOptionLabel={(option) => `${option?.firstName} ${option?.lastName}`}*/}
            {/*    groupBy={(option) =>*/}
            {/*      option?.lastName?.[0]?.toUpperCase() ||*/}
            {/*      option?.firstName?.[0]?.toUpperCase()*/}
            {/*    }*/}
            {/*  />*/}
            {/*</Grid>*/}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={salesReps}
                onChange={setSalesReps}
                options={salesRepsOptions}
                label="Sales Reps"
                getOptionLabel={(option) => `${option?.firstName} ${option?.lastName} (${option?.email})`}
                groupBy={(option) =>
                  option?.lastName?.[0]?.toUpperCase() ||
                  option?.firstName?.[0]?.toUpperCase()
                }
                multiple
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={registrars}
                onChange={setRegistrars}
                options={registrarsOptions}
                label="Registrars"
                getOptionLabel={(option) => `${option?.firstName} ${option?.lastName} (${option?.email})`}
                groupBy={(option) =>
                  option?.lastName?.[0]?.toUpperCase() ||
                  option?.firstName?.[0]?.toUpperCase()
                }
                multiple
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={managers}
                onChange={setManagers}
                options={managersOptions}
                label="Practice Managers"
                getOptionLabel={(option) => `${option?.firstName} ${option?.lastName} (${option?.email})`}
                groupBy={(option) =>
                  option?.lastName?.[0]?.toUpperCase() ||
                  option?.firstName?.[0]?.toUpperCase()
                }
                multiple
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SelectBox
                label="Hospitals"
                selected={(hospitals || []).filter(Boolean).map((hospital) => ({
                  ...hospital,
                  label: hospital?.name || '',
                }))}
                onClick={() => setView(1)}
              />
            </Grid>
          </Grid>
        ) : (
          <SelectBoxList
            sx={{ width: '560px' }}
            title="Select Hospitals"
            onBackClick={() => setView(0)}
            search={search}
            setSearch={setSearch}
            onChange={(selected) => {
              onHospitalSelect(selected);
              setSearch('');
            }}
            searchLabel="Add Hospital"
            options={hospitalsOptions}
            selected={sortBy((hospitals || []).filter(Boolean), 'name')}
            onRemoveItem={onHospitalRemove}
          />
        )}
      </Box>
    </Drawer>
  );
};

SurgeonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default SurgeonModal;
